// `.loom.yml` schema.
//
// The schema is intentionally strict (`.strict()` on each object): unknown
// fields are rejected with a typed error rather than silently dropped. This
// matters because users will copy/paste partial configs from blog posts and
// older versions; a missing typo signal makes "my config doesn't work" a hard
// debugging session.
//
// Two product-level commitments are encoded as schema-level rejections:
//
//   1. `quality_check.advisory_only` does not exist. The quality coach is
//      advisory by architecture. If someone writes that field
//      hoping to flip it to `false` and gate transitions on rubric scores,
//      the schema rejects it with a hint.
//
//   2. Rubric family names are a closed v1 registry. Unknown families fail
//      validation with a hint that lists the registry.
//      Adding a family requires editing this registry deliberately, in a PR.

import { z } from "zod";
import { localeSchema } from "../i18n.js";
import { LabelVocabularySchema } from "../labels/index.js";
import { breakdownConfigSchema } from "../readiness/sizing.js";
import { readinessSpecSchema } from "../readiness/spec.js";

// ---------------------------------------------------------------------------
// Top-level fixed-value sets
// ---------------------------------------------------------------------------

export const METHODOLOGIES = ["scrumban", "scrum", "kanban", "custom"] as const;
export type Methodology = (typeof METHODOLOGIES)[number];

export const EXECUTORS = ["loom-hosted", "ci-job"] as const;
export type ExecutorChoice = (typeof EXECUTORS)[number];

export const ADR_STORAGE_MODES = ["gitlab-issue", "repo-markdown", "both"] as const;
export type AdrStorage = (typeof ADR_STORAGE_MODES)[number];

// Closed v1 rubric registry. Adding a family
// is a deliberate PR: a new entry here, plus prompt block, verdict schema,
// and golden fixtures under packages/workflows/test/golden/quality/.
export const RUBRIC_FAMILIES = [
  "invest",
  "qus",
  "smart",
  "gherkin_testability",
  "dod_mentions",
  "decision_links",
] as const;
export type RubricFamily = (typeof RUBRIC_FAMILIES)[number];

const RubricFamilySchema = z.enum(RUBRIC_FAMILIES, {
  errorMap: () => ({
    message: `unknown rubric family. v1 closed registry: ${RUBRIC_FAMILIES.join(", ")}.`,
  }),
});

// ---------------------------------------------------------------------------
// Sub-schemas
// ---------------------------------------------------------------------------

const nonEmptyString = z.string().trim().min(1);

const IterationSchema = z
  .object({
    cadence_weeks: z.number().int().positive(),
  })
  .strict();

const EnrichmentSchema = z
  .object({
    ledger_issue_title: nonEmptyString.optional(),
    modernize_on_touch: z.boolean().optional(),
  })
  .strict();

const QualityCheckRubricSchema = z
  .object({
    issue: z.array(RubricFamilySchema).optional(),
    acceptance_criteria: z.array(RubricFamilySchema).optional(),
    merge_request: z.array(RubricFamilySchema).optional(),
  })
  .strict();

const QualityCheckThresholdsSchema = z
  .object({
    warn_below_score: z.number().int().min(0).max(100),
    fail_below_score: z.number().int().min(0).max(100),
  })
  .strict()
  .refine((t) => t.fail_below_score <= t.warn_below_score, {
    message:
      "fail_below_score must be <= warn_below_score (fail is the stronger banner).",
  });

// Quality-check sub-schema. We use `.passthrough()` + a custom refinement
// instead of `.strict()` so that the rejection messages can be precise:
//
//   - `advisory_only` gets a hint that states the design decision.
//   - Other unknown keys get a generic-but-actionable "unknown field" error.
const QUALITY_CHECK_KNOWN_KEYS = new Set(["enabled", "rubric", "thresholds"]);
const QualityCheckSchema = z
  .object({
    enabled: z.boolean().optional(),
    rubric: QualityCheckRubricSchema.optional(),
    thresholds: QualityCheckThresholdsSchema.optional(),
  })
  .passthrough()
  .superRefine((value, ctx) => {
    for (const key of Object.keys(value)) {
      if (key === "advisory_only") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["advisory_only"],
          message:
            "`quality_check.advisory_only` is not configurable. The quality coach is advisory by architecture; thresholds drive surfacing, never blocking.",
        });
        continue;
      }
      if (!QUALITY_CHECK_KNOWN_KEYS.has(key)) {
        ctx.addIssue({
          code: z.ZodIssueCode.unrecognized_keys,
          keys: [key],
          message: `Unknown field \`quality_check.${key}\`.`,
        });
      }
    }
  });

// ---------------------------------------------------------------------------
// Dashboard mapping — per-project label → Loom stage projection
// ---------------------------------------------------------------------------
//
// GitLab issues are `opened` or `closed`. The customer-oriented project
// dashboard renders five Loom stages — `needs_input → planned → in_progress
// → review → shipped`. The mapping from labels to stages is project-specific:
// some teams use `workflow::ready`, others `kanban::up-next`, others
// `state::in-progress`. This block lets the project owner declare which
// labels belong to which stage. The order in which `stage_mapping` is
// evaluated is the order of `DASHBOARD_STAGES` (later stages win — an issue
// with both an in-progress and a review label is treated as "review").
//
// `decision_labels` are labels that mark an issue as "needing the customer's
// call" — they bubble it up into the dashboard's decisions inbox.

const labelPatternList = z.array(nonEmptyString);

const DashboardStageMappingSchema = z
  .object({
    needs_input: labelPatternList.optional(),
    planned: labelPatternList.optional(),
    in_progress: labelPatternList.optional(),
    review: labelPatternList.optional(),
    shipped: labelPatternList.optional(),
  })
  .strict();

export type DashboardStageMapping = z.infer<typeof DashboardStageMappingSchema>;

const DashboardConfigSchema = z
  .object({
    stage_mapping: DashboardStageMappingSchema.optional(),
    decision_labels: labelPatternList.optional(),
  })
  .strict();

export type DashboardConfig = z.infer<typeof DashboardConfigSchema>;

// ---------------------------------------------------------------------------
// Top-level schema
// ---------------------------------------------------------------------------

export const LoomConfigSchema = z
  .object({
    methodology: z.enum(METHODOLOGIES).optional(),
    executor: z.enum(EXECUTORS).optional(),
    // Content locale — the language issue prose (narrative, questions, chat) is
    // authored in. Per-project, session-immutable (pinned at session create).
    // Does NOT localize the reasoning plane (prompts/rubrics) or structural
    // scaffolding (headings/labels), only user-facing prose.
    //
    // ABSENT IS NOT "ENGLISH". With no key the agent is told to write in the
    // interface language the user opened the session from, and to follow a
    // user who writes to it in another one (`resolveSessionLocale` →
    // `localePromptDirective` at the default locale); the written story frame
    // follows the language of its own clauses (`resolveContentLocale`).
    // `locale: en` makes the DIRECTIVE name English whatever the interface
    // language is — still following a user who writes in another language —
    // and is otherwise byte-identical to an absent key (`asLocale`): it pins no
    // English frame. Setting `de` pins German, absolutely, whatever the content
    // or the interface says — the right control for a monolingual project and
    // the wrong one for a project that writes both languages.
    locale: localeSchema.optional(),
    adr_storage: z.enum(ADR_STORAGE_MODES).optional(),
    adr_path: nonEmptyString.optional(),
    labels: LabelVocabularySchema.optional(),
    definition_of_ready: z.array(nonEmptyString).optional(),
    definition_of_done: z.array(nonEmptyString).optional(),
    // Per-project readiness-card requirements (the refine gate's field list).
    // Optional: absent → the built-in `DEFAULT_READINESS_SPEC`. A malformed
    // value here is tolerated by `resolveReadinessSpec` (safeParse → default),
    // so it can never crash the gate. v1 ships no project spec end-to-end; the
    // apply-mapping a custom field would also need stays follow-up.
    readiness_spec: readinessSpecSchema.optional(),
    // Per-project size-estimation / split-into-sub-issues policy (the refine
    // tail's "should this ticket be an epic" step). Optional: absent → the
    // built-in `DEFAULT_BREAKDOWN_POLICY`. A malformed value is tolerated by
    // `resolveBreakdownPolicy` (safeParse → default), never crashing the gate.
    breakdown: breakdownConfigSchema.optional(),
    iteration: IterationSchema.optional(),
    enrichment: EnrichmentSchema.optional(),
    quality_check: QualityCheckSchema.optional(),
    dashboard: DashboardConfigSchema.optional(),
    // Figma design ingestion — the per-project file-key allowlist (`figma.files`).
    //
    // DELIBERATELY `z.unknown()`, not the real shape. This schema is `.strict()`
    // and `apps/server/src/agent/load-project-config.ts` falls back to
    // `LOOM_CONFIG_DEFAULTS` for the WHOLE DOCUMENT on any parse failure, so a
    // typo'd sub-key (`file:` for `files:`) declared here would silently cost the
    // project its `readiness_spec`, `locale`, label vocabulary, breakdown policy
    // and dashboard mapping. Declaring the key permissively makes its PRESENCE
    // legal; its CONTENT is parsed separately and non-strictly by
    // `apps/server/src/figma/project-config.ts`, which fails CLOSED (an
    // unparseable block allows no file) without touching anything else. Same
    // tolerance stance `readiness_spec` and `breakdown` take above, one step
    // further: those tolerate a malformed VALUE, this tolerates a malformed
    // SHAPE, because the value is a security gate and must not be co-parsed
    // with the rest of the document.
    figma: z.unknown().optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    // `adr_path` is only meaningful when ADR storage involves repo markdown.
    if (
      value.adr_path !== undefined &&
      value.adr_storage !== undefined &&
      value.adr_storage === "gitlab-issue"
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["adr_path"],
        message:
          "`adr_path` is only used when `adr_storage` includes repo-markdown (`repo-markdown` or `both`).",
      });
    }
  });

export type LoomConfig = z.infer<typeof LoomConfigSchema>;

// ---------------------------------------------------------------------------
// Defaults — applied when a field is absent. The defaults below match the
// architecture's "Scrumban + standard vocabulary" baseline. They are not
// merged into the parsed value automatically; callers that need the
// fully-resolved view use `resolveLoomConfig()` below.
// ---------------------------------------------------------------------------

export const LOOM_CONFIG_DEFAULTS = {
  methodology: "scrumban" as Methodology,
  executor: "loom-hosted" as ExecutorChoice,
  adr_storage: "both" as AdrStorage,
  adr_path: "docs/adr",
  iteration: { cadence_weeks: 2 },
  enrichment: {
    ledger_issue_title: "meta::enrichment-ledger",
    modernize_on_touch: true,
  },
  quality_check: {
    enabled: true,
    rubric: {
      issue: ["invest", "qus"] satisfies RubricFamily[],
      acceptance_criteria: ["smart", "gherkin_testability"] satisfies RubricFamily[],
      merge_request: ["dod_mentions", "decision_links"] satisfies RubricFamily[],
    },
    thresholds: { warn_below_score: 70, fail_below_score: 40 },
  },
} as const;

/**
 * Parse a raw value (typically the result of YAML.parse on a `.loom.yml`).
 * Returns the typed `LoomConfig`. Throws a `ZodError` with the schema-level
 * issues if the input is invalid. The error message is designed to be shown
 * to the user verbatim.
 */
export const parseLoomConfig = (raw: unknown): LoomConfig => LoomConfigSchema.parse(raw);

/**
 * Like `parseLoomConfig` but returns a `SafeParseReturnType` for callers that
 * want to render their own diff-style error UI.
 */
export const safeParseLoomConfig = (
  raw: unknown,
): z.SafeParseReturnType<unknown, LoomConfig> => LoomConfigSchema.safeParse(raw);
